import { FC } from 'react';
import { Label, Toggle } from '@silverfort/sf-components';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { AccountMatchingSettingsForm } from '../types';
import SettingsTextInput from './SettingsTextInput';

const AccountMatchingSection: FC = () => {
    const { control } = useFormContext<AccountMatchingSettingsForm>();
    const isAccountMatchingEnabled = useWatch({ name: 'isAccountMatchingEnabled', control });

    return (
        <div className="rounded-[8px] border border-sf-gray-100 p-6 flex flex-col gap-6">
            <div className={`flex ${isAccountMatchingEnabled ? 'items-start' : 'items-center'}`}>
                <Label
                    className={`w-[235px] ${isAccountMatchingEnabled ? '!items-start' : '!items-center'}`}
                    text="Account matching"
                />
                <div className="flex flex-col gap-6">
                    <Controller
                        control={control}
                        name="isAccountMatchingEnabled"
                        render={({ field: { value, onChange } }) => (
                            <Toggle
                                // todo: remove the "mb-0" after https://silverfort.atlassian.net/browse/AF-36781
                                className="mb-0"
                                checked={value}
                                label="Enable account matching"
                                onChange={onChange}
                            />
                        )}
                    />
                    {isAccountMatchingEnabled && (
                        <Controller
                            control={control}
                            name="shouldOverrideManualMatches"
                            render={({ field: { value, onChange } }) => (
                                <Toggle
                                    // todo: remove the "mb-0" after https://silverfort.atlassian.net/browse/AF-36781
                                    className="mb-0"
                                    checked={value}
                                    label="Override manually matched users"
                                    onChange={onChange}
                                    tooltipInfo="Enable to apply the configuration to manually matched users. If disabled, it applies only to users who were not manually matched."
                                />
                            )}
                        />
                    )}
                </div>
            </div>
            {isAccountMatchingEnabled && (
                <div className="flex flex-col gap-6">
                    <div className="flex">
                        <Label className="w-[235px] !items-start" text="AD fields" />
                        <div className="flex flex-col gap-4">
                            <Controller
                                control={control}
                                name="adFieldsConfig.matchAttribute"
                                rules={{ required: 'Match attribute is required' }}
                                render={({ field: { value, onChange, onBlur }, fieldState }) => (
                                    <SettingsTextInput
                                        label="Attribute"
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        errorMessage={fieldState.error?.message}
                                        labelTooltip="This AD attribute determines how users in your organization are matched to their corresponding accounts"
                                    />
                                )}
                            />
                            <div className="flex gap-12">
                                <Controller
                                    control={control}
                                    name="adFieldsConfig.primaryUserIdentifyAttribute"
                                    rules={{ required: 'Primary user attribute identifier is required' }}
                                    render={({ field: { value, onChange, onBlur }, fieldState }) => (
                                        <SettingsTextInput
                                            label="Primary user attribute identifier"
                                            value={value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            errorMessage={fieldState.error?.message}
                                            labelTooltip="AD attribute determines which of the matched users is the primary of the account"
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="adFieldsConfig.primaryUserIdentifyValue"
                                    rules={{ required: 'Primary user value identifier is required' }}
                                    render={({ field: { value, onChange, onBlur }, fieldState }) => (
                                        <SettingsTextInput
                                            label="Primary user value identifier"
                                            value={value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            errorMessage={fieldState.error?.message}
                                            labelTooltip="Value determines which users are the primary and secondary of the matched accounts"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <Label className="min-h-10 w-[235px]" text="Case sensitive" />
                        <Controller
                            control={control}
                            name="adFieldsConfig.isCaseSensitive"
                            render={({ field: { value, onChange } }) => (
                                <Toggle
                                    // todo: remove the "mb-0" after https://silverfort.atlassian.net/browse/AF-36781
                                    className="mb-0"
                                    checked={value}
                                    label="Enable case sensitivity for the attribute values"
                                    onChange={onChange}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountMatchingSection;
